import React, { useEffect, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Row, Select, Table, Tooltip, DatePicker } from 'antd';
import moment from 'moment';
//
import {
  showTotal,
  getInitialSearch,
  setSearchInLocalStorage,
  sortColumnObject,
} from 'util/helpers';
import { setLoading } from 'appRedux/reducers/Loading';
import { fetchActivity, fetchOffices, fetchCompanies, fetchStaticItem } from 'appRedux/actions';
import { PAGINATION_TYPES, PAGINATION_LIMIT, DATE_FORMAT, viewDateFormat, viewTimeFormat } from 'util/constants';
import { REST_API } from 'services/rest.api';
import { ActivityService } from 'services';
import Widget from 'components/Widget';
import { EmployeeSearchBox } from 'components/SearchBox';
import EmployeeCell from 'components/EmployeeCell';
import Auxiliary from 'util/Auxiliary';
import { ExportOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';

const loadingObj = {
  key: PAGINATION_TYPES.activity,
  value: true
};

const Activity = () => {
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const user = JSON.parse(localStorage.getItem('currentUser'));
  const { employee_id: employeeId } = user || {};
  const [search, setSearch] = useState({
    ...getInitialSearch({
      employee_id: '',
      company_id: '',
      office_id: '',
      category_id: '',
      date_range: []
    }, url),
    employee_id: '',
  });
  const [current, setCurrent] = useState(1);
  const [sort, setSort] = useState(JSON.parse(localStorage.getItem(`${url}/sort`)) || '');

  const getActivityList = React.useCallback(
    (_page = 1, limit = PAGINATION_LIMIT) => {
      setCurrent(_page);
      const query = {
        page: _page,
        limit,
        sort: 'id,desc',
        descendants: true,
        ...search,
        employee_id: search?.employee_id || employeeId,
        ...(sort && { sort }),
      };
      dispatch(fetchActivity(query));
    },
    [dispatch, search, sort, employeeId],
  );

  useEffect(() => {
    if (search) {
      setSearchInLocalStorage(search, url);
    }
  }, [search, url]);

  React.useEffect(() => {
    getActivityList();
  }, [getActivityList]);

  useEffect(() => {
    if (sort) {
      localStorage.setItem(`${url}/sort`, JSON.stringify(sort));
    }
  }, [sort]);

  React.useEffect(() => {
    dispatch(fetchOffices());
    dispatch(fetchCompanies());
    dispatch(fetchStaticItem(REST_API.ACTIVITY.CATEGORIES, ActivityService));
  }, [dispatch]);

  const handleExportCSV = async () => {
    try {
      dispatch(setLoading(loadingObj));
      if (!search?.date_range) {
        search.date_range = [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')];
      }
      const res = await ActivityService.find({ ...search, descendants: true, employee_id: search?.employee_id || employeeId, sort: 'id,desc', response_type: 'csv' });
      var downloadLink = document.createElement('a');
      var blob = new Blob(['\ufeff', res]);
      var url = URL.createObjectURL(blob);
      downloadLink.download = 'activity.csv';
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      //
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };

  const { isLoading, activity, offices, companies, activityCategories } = useSelector(({ pagination, common, loading }) => ({
    activity: pagination[PAGINATION_TYPES.activity],
    activityCategories: common[REST_API.ACTIVITY.CATEGORIES],
    offices: pagination[PAGINATION_TYPES.offices],
    companies: pagination[PAGINATION_TYPES.companies],
    isLoading: loading[PAGINATION_TYPES.activity] || loading[PAGINATION_TYPES.offices],
  }));

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employee',
      width: 200,
      key: 'name',
      render: (_) => {
        const { id, name, photo, eid } = _ ?? {};
        return (
          <Link to={{ pathname: `/profile/${id}`, state: { tab: 'activity' } }}>
            <EmployeeCell {...{ name, photo: photo?.small, eid }} />
          </Link>
        );
      },
      ...sortColumnObject('employee_id', setSort, sort),
    },
    {
      title: 'Company',
      dataIndex: ['employee', 'company', 'name'],
      width: 200,
      key: 'company',
      ...sortColumnObject('company_id', setSort, sort),
    },
    {
      title: 'Location',
      dataIndex: 'address',
      key: 'location',
    },
    {
      title: 'Activity Type',
      dataIndex: ['type', 'name'],
      width: 200,
      key: 'activity',
      ...sortColumnObject('type_id', setSort, sort),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 200,
      key: 'description',
    },
    {
      title: 'Created At',
      dataIndex: 'created',
      width: 200,
      key: 'created',
      render: (_) => `${viewDateFormat(_)} ${viewTimeFormat(_)}`,
      ...sortColumnObject('created', setSort, sort),
    },
    {
      title: 'Activity',
      dataIndex: ['category', 'name'],
      width: 200,
      key: 'activity'
    },
  ];
  const pagination = {
    current,
    hideOnSinglePage: true,
    position: 'both',
    total: activity?.total || 0,
    showTotal,
    pageSizeOptions: [10, 20, 50],
    showQuickJumper: true,
    onChange: (page, pageSize) => getActivityList(page, pageSize)
  };

  const handleChange = (value = '', key) => {
    setSearch((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleDateChange = (dates, key) => {
    if (!dates?.length) {
      handleChange([], key);
      return;
    }
    handleChange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')], key);
  };

  const renderActivity = () => (
    <Row>
      <Col
        span={4}
        className='gx-mb-4'
      >
        <EmployeeSearchBox
          value={search?.employee_id}
          onChange={item => handleChange(item?.value?.split('#')[0], 'employee_id')}
        />
      </Col>
      <Col
        span={4}
        className='gx-mb-4'
      >
        <Select
          showSearch
          allowClear
          className="gx-w-100"
          placeholder="Select a company"
          optionFilterProp="children"
          onChange={item => handleChange(item, 'company_id')}
          defaultValue={search?.company_id === '' ? null : search?.company_id}
          filterOption={(input, option) => option?.children?.toLowerCase()?.includes(input?.toLowerCase())}
        >
          {companies?.items?.map((item, index) => (
            <Select.Option
              key={index}
              value={item.id}
            >
              {item.name}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col
        span={4}
        className='gx-mb-4'
      >
        <Select
          showSearch
          allowClear
          className="gx-w-100"
          placeholder="Select a office"
          optionFilterProp="children"
          onChange={item => handleChange(item, 'office_id')}
          defaultValue={search?.office_id === '' ? null : search?.office_id}
          filterOption={(input, option) => option?.children?.toLowerCase()?.includes(input?.toLowerCase())}
        >
          {offices?.items?.map((item, index) => (
            <Select.Option
              key={index}
              value={item.id}
            >
              {item.name}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col
        span={4}
        className='gx-mb-4'
      >
        <Select
          allowClear
          className="gx-w-100"
          placeholder="Select category"
          onChange={item => handleChange(item?.value, 'category_id')}
          defaultValue={search?.category_id === '' ? null : search?.category_id}
          options={activityCategories?.map(i => ({ value: i.id, label: i.name }))}
          labelInValue
        />
      </Col>
      <Col
        span={5}
        className='gx-mb-4'
      >
        <RangePicker
          ranges={{
            'Today': [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          placeholder={['Start date', 'End date']}
          format={DATE_FORMAT}
          onChange={v => handleDateChange(v, 'date_range')}
          defaultValue={search?.date_range?.length > 0 && [moment(search?.date_range[0], dateFormat), moment(search?.date_range[1], dateFormat)]}
        />
      </Col>
      <Col
        span={3}
        className='gx-mb-4'
      >
        <Tooltip title="Export CSV File">
          <Button
            loading={isLoading}
            type="primary"
            onClick={() => { handleExportCSV(); }}
            icon={<ExportOutlined />}
          >Export</Button>
        </Tooltip>
      </Col>
      <Col span={24}>
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={activity?.items || []}
          pagination={pagination}
          rowKey={item => item.id}
        />
      </Col>
    </Row>
  );

  return (
    <Auxiliary>
      <Widget>
        {renderActivity()}
      </Widget>
    </Auxiliary>
  );
};

export default Activity;
